import { useCallback, useEffect, useMemo } from "react";
import { Grid } from "@mui/material";
import ContentTitle from "../../../../components/Content/ContentTitle";
import { INavigationLink } from "../../../../interfaces/INavigation";
import { useAuthContext } from "../../../../contexts/AuthContext";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import { ServiceReports } from '../../../../data/Reports';
import DefaultConstants from "../../../../data/Constants";
import IReport from '../../../../interfaces/IReport';
import { useReportPreviewContext } from '../../../../contexts/ReportPreviewContext';
import IUser from '../../../../interfaces/IUser';
import FormHelper from '../../../../helpers/FormHelper';
import UserService from '../../../../services/UserService';
import IAccessProfile from '../../../../interfaces/IAccessProfile';
import AccessProfileService from '../../../../services/AccessProfileService';
import DataCard from '../../../../components/DataCard';
import useDialog from "../../../../hooks/useDialog";
import useState from "../../../../hooks/useState";


interface IStateValues {
    selectedReport: IReport | undefined;
    userList: IUser[];
    accessProfileList: IAccessProfile[];
}

const Reports = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { user, enterprise, hasPermission } = useAuthContext();
    const { handleOpen } = useReportPreviewContext();

    const state = useState<IStateValues>({
        selectedReport: undefined,
        userList: [],
        accessProfileList: []
    });
    
    const filtersDialog = useDialog();

    
    const reportList = useMemo(() => {
        return ServiceReports.filter(r => hasPermission(r.alias));
        // eslint-disable-next-line
    }, []);


    const enterpriseList = useMemo(() => {
        return user?.enterprises.filter(e => e.permissions.map(p => p.alias).includes(state.values.selectedReport?.alias ?? '')) ?? [];
    }, [user, state.values.selectedReport]);


    useEffect(() => {
        if (state.values.userList.length === 0) {
            const formData = FormHelper.Create(undefined);
            UserService.List(formData)
                .then((resp) => {
                    state.update('userList', resp.data.users as IUser[]);
                });
        }

        if (state.values.accessProfileList.length === 0) {
            const formData = FormHelper.Create(undefined);
            AccessProfileService.List(formData)
                .then((resp) => {
                    state.update('accessProfileList', resp.data.accessProfiles as IAccessProfile[]);
                });
        }
        // eslint-disable-next-line
    }, []);


    const handleChangeSelectedReport = useCallback((report: IReport) => {
        state.update('selectedReport', report);
        filtersDialog.handleOpen();
        // eslint-disable-next-line
    }, []);


    const handleOpenPreview = useCallback((report: IReport, filters: any) => {
        handleOpen(report, filters);
        // eslint-disable-next-line
    }, []);


    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle
                        name={translate(linkProps.name)} 
                        description={enterprise?.name}
                        applyTranslation={false}
                    />
                </Grid>
            </Grid>

            <Grid container
                className='grid-content overflow-enabled justifycontent-flexstart aligncontent-flexstart' 
                rowSpacing={DefaultConstants.gridRowSpacing} 
                columnSpacing={DefaultConstants.gridColumnSpacing}
            >
                {
                    reportList.map(report => {
                        return <Grid item lg={3} md={4} sm={6} xs={12} key={report.alias}>
                            <DataCard 
                                variant='default'
                                variantProps={{
                                    default: {
                                        title: translate(report.title),
                                        subtitle: report.subtitle ? translate(report.subtitle) : undefined
                                    }
                                }}
                                Icon={report.Icon}
                                onClick={() => handleChangeSelectedReport(report)}
                            />
                        </Grid>;
                    })
                }
            </Grid>

            {
                state.values.selectedReport && <state.values.selectedReport.Filters 
                    open={filtersDialog.open}
                    handleClose={() => filtersDialog.handleClose()}
                    handleOpenPreview={(filters: any) => handleOpenPreview(state.values.selectedReport as IReport, filters)}
                    enterpriseList={enterpriseList}
                    userList={state.values.userList}
                    accessProfileList={state.values.accessProfileList}
                />
            }
        </>
    );
}

export default Reports;